import { normalizePlatformKey } from '@/config/socialPlatforms'

export const LEGACY_SOCIAL_KEYS = [
    'facebook',
    'twitter',
    'instagram',
    'youtube',
    'github',
    'behance',
    'linkedin',
    'spotify',
    'tumblr',
    'telegram',
    'pinterest',
    'snapchat',
    'reddit',
    'google',
    'apple',
    'figma',
    'discord',
    'tiktok',
    'whatsapp',
    'skype',
    'google_scholar',
    'medium',
    'wechat',
]

export function createSocialEntry(platform, url = '', label = '') {
    const key = normalizePlatformKey(platform)
    return {
        id: `${key}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        platform: key,
        url: url || '',
        label: label || '',
    }
}

function isHttpUrl(value) {
    return (
        typeof value === 'string' && /^https?:\/\//i.test(value.trim())
    )
}

function normalizeUrlKey(url) {
    return String(url || '')
        .trim()
        .toLowerCase()
        .replace(/\/+$/, '')
}

/** Collect every social URL for public card display (social_links + legacy columns). */
export function parseSocialLinksFromQr(qr) {
    if (!qr) return []

    const entries = []
    const seenUrls = new Set()

    const addLink = (platform, url, label = '', id = null) => {
        const trimmedUrl = String(url || '').trim()
        if (!trimmedUrl) return

        const urlKey = normalizeUrlKey(trimmedUrl)
        if (seenUrls.has(urlKey)) return
        seenUrls.add(urlKey)

        const platformKey = normalizePlatformKey(platform || 'link')
        entries.push({
            id:
                id ||
                `${platformKey}-${entries.length}-${urlKey.slice(-16)}`,
            platform: platformKey,
            url: trimmedUrl,
            label: String(label || '').trim(),
        })
    }

    let links = qr.social_links
    if (typeof links === 'string') {
        try {
            links = JSON.parse(links)
        } catch {
            links = []
        }
    }

    if (Array.isArray(links)) {
        links.forEach(item => {
            if (!item) return

            const platform = item.platform || 'link'
            const url = item.url
            const rawLabel = item.label

            addLink(platform, url, isHttpUrl(rawLabel) ? '' : rawLabel, item.id)

            // Label field sometimes holds a second URL (e.g. business page).
            if (
                isHttpUrl(rawLabel) &&
                normalizeUrlKey(rawLabel) !== normalizeUrlKey(url)
            ) {
                addLink(platform, rawLabel, 'Business', `${item.id || platform}-alt`)
            }
        })
    }

    LEGACY_SOCIAL_KEYS.forEach(key => {
        addLink(key, qr[key], '')
    })

    return entries
}

export function hasSocialLinks(qr) {
    return parseSocialLinksFromQr(qr).length > 0
}

export function getFilledSocialLinks(entries) {
    return (entries || []).filter(entry => entry?.url?.trim())
}

export function syncLegacyFieldsFromLinks(entries) {
    const out = {}
    LEGACY_SOCIAL_KEYS.forEach(key => {
        out[key] = ''
    })

    getFilledSocialLinks(entries).forEach(entry => {
        const platform = entry.platform?.toLowerCase()
        const url = entry.url?.trim()
        if (platform && url && !out[platform]) {
            out[platform] = url
        }
    })

    return out
}

export function appendSocialLinksToFormData(formData, entries) {
    const filled = getFilledSocialLinks(entries).map(({ id, platform, url, label }) => ({
        id,
        platform,
        url: url.trim(),
        label: (label || '').trim(),
    }))

    formData.append('social_links', JSON.stringify(filled))

    const legacy = syncLegacyFieldsFromLinks(filled)
    LEGACY_SOCIAL_KEYS.forEach(key => {
        formData.append(key, legacy[key] || '')
    })
}

export const SOCIAL_ICON_IMG = {
    facebook: '/img/icons/facebook.svg',
    twitter: '/img/icons/twitter.svg',
    instagram: '/img/icons/instagram.svg',
    behance: '/img/icons/behance.svg',
    linkedin: '/img/icons/linkedin.svg',
    google: '/img/icons/google.svg',
    youtube: '/img/icons/youtube.svg',
    apple: '/img/icons/apple.svg',
    snapchat: '/img/icons/snapchat.svg',
    figma: '/img/icons/figma.svg',
    reddit: '/img/icons/reddit.svg',
    discord: '/img/icons/discord.svg',
    tiktok: '/img/icons/tiktok.svg',
    tumblr: '/img/icons/tumblr.svg',
    telegram: '/img/icons/telegram.svg',
    pinterest: '/img/icons/pinterest.svg',
    github: '/img/icons/githup.svg',
    messenger: '/img/icons/messenger.svg',
    whatsapp: '/img/icons/whatsapp.svg',
    skype: '/img/icons/skype.svg',
    spotify: '/img/icons/spotify.svg',
    google_scholar: '/img/icons/scholar.svg',
    medium: '/img/icons/medium.svg',
    wechat: '/img/icons/wechat.svg',
}
